import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppDataContext } from '../context/AppDataContext';
import { supabase } from '../supabaseClient';
import LevelUpModal from '../components/LevelUpModal';
import ShoreScene from '../components/bridge/ShoreScene';
import { skyFor } from '../constants/sky';
import NoteLetter from '../components/bridge/NoteLetter';
import HowWasItSheet from '../components/bridge/HowWasItSheet';
import { LEVELS } from '../constants/levels';
import { FEELINGS, feelingById, firstName, type FeelingId, type MissionReflection } from '../constants/bridge';
import type { Mission } from '../types';
import Critter from '../components/glyphs/Critter';
import Glyph from '../components/glyphs/Glyph';
import BridgeNavIcon from '../components/BridgeNavIcon';
import { useWeeklyJourney, weekStart } from '../hooks/useWeeklyJourney';
import { useRiverStones } from '../hooks/useRiverStones';
import { play, setSoundOn, soundOn } from '../lib/sound';

const LAST_VISIT = 'remind_last_visit';

// Was the last visit more than two days ago? The visit itself is stamped in an effect.
const readWelcomeBack = () => {
  try {
    const last = Number(localStorage.getItem(LAST_VISIT));
    return last > 0 && Date.now() - last > 2 * 864e5;
  } catch {
    return false;
  }
};

const QUOTES = [
  'כל צעד קטן הוא חלק מהמסע הגדול.',
  'יש בך יותר כוח ממה שנדמה לך.',
  'הצלחה היא סך כל המאמצים הקטנים שנעשים יום יום.',
  'היום הוא הזדמנות חדשה לצמוח.',
  'נשימה עמוקה אחת יכולה לשנות הכל.',
];

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

// ─── SkyPicker ────────────────────────────────────────────────────────────────
// Picking a feeling repaints the sky above it; that is the whole confirmation.
const SkyPicker: React.FC<{ userId: string; value: FeelingId | null; onPick: (f: FeelingId) => void }> = ({ userId, value, onPick }) => {
  const [saved, setSaved] = useState(false);

  const handleSelect = async (w: (typeof FEELINGS)[number]) => {
    onPick(w.id);
    setSaved(true);
    try {
      await supabase.from('mood_logs').insert({ user_id: userId, mood_score: w.score, mood_type: w.id });
    } catch { /* silent */ }
  };

  return (
    <section aria-labelledby="sky-title">
      <div className="flex items-baseline justify-between mb-2">
        <h2 id="sky-title" className="text-sm font-bold text-foreground">איך השמיים שלך עכשיו?</h2>
        {saved && <span className="text-xs text-muted-foreground" role="status">נשמר, גם אצל המטפל/ת</span>}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {FEELINGS.map((w) => {
          const isActive = value === w.id;
          const tone = skyFor(w.id, 12);
          return (
            <motion.button
              key={w.id}
              onClick={() => handleSelect(w)}
              aria-pressed={isActive}
              whileTap={{ scale: 0.92 }}
              className={`relative overflow-hidden flex flex-col items-center gap-1 rounded-2xl pt-2.5 pb-2 border-2 transition-colors ${
                isActive ? 'border-foreground' : 'border-transparent'
              }`}
              style={{ background: `linear-gradient(${tone.top}, ${tone.bottom})` }}
            >
              <Glyph name={w.glyph} size={28} />
              <span className={`text-[11px] font-bold ${tone.dark ? 'text-white' : 'text-foreground'}`}>{w.label}</span>
            </motion.button>
          );
        })}
      </div>
      {saved && (value === 'stormy' || value === 'rainy') && (
        <p className="mt-2 text-xs text-muted-foreground" role="note">
          המטפל/ת יראו את זה לפני הפגישה, לא מיד. אם קשה עכשיו ממש: ער״ן <a href="tel:1201" className="font-bold underline">1201</a>, בכל שעה.
        </p>
      )}
    </section>
  );
};

// ─── PatientHome ──────────────────────────────────────────────────────────────
const PatientHome: React.FC = () => {
  const navigate = useNavigate();
  const { profile, missions, loading, completeMission, showLevelUp, closeLevelUp } = useAppDataContext();
  const [therapistFull, setTherapistFull] = useState<string | null>(null);
  const [reflections, setReflections] = useState<MissionReflection[]>([]);
  const [asking, setAsking] = useState<Mission | null>(null);
  const [announce, setAnnounce] = useState('');
  const [sky, setSky] = useState<FeelingId | null>(null);
  const [flightKey, setFlightKey] = useState(0);
  const [welcomeBack] = useState(readWelcomeBack);
  useEffect(() => {
    try { localStorage.setItem(LAST_VISIT, String(Date.now())); } catch { /* private mode */ }
  }, []);
  const weekPlanks = useWeeklyJourney(profile?.id, flightKey)?.[0]?.laid;
  const stones = useRiverStones(profile?.id, flightKey);
  const [sound, setSound] = useState(soundOn);
  const [celebrate, setCelebrate] = useState<string | null>(null);

  const quote = useMemo(() => QUOTES[Math.floor(Math.random() * QUOTES.length)], []);

  // The other end of the bridge: who sent these notes, and what came back from them.
  useEffect(() => {
    if (!profile) return;
    if (profile.therapist_id) {
      supabase.from('profiles').select('full_name').eq('id', profile.therapist_id).maybeSingle()
        .then(({ data }: { data: { full_name?: string } | null }) => setTherapistFull(data?.full_name ?? null));
    }
    supabase.from('mood_logs').select('mood_type, mood_score, created_at')
      .eq('user_id', profile.id).gte('created_at', startOfToday())
      .order('created_at', { ascending: false }).limit(1)
      .then(({ data }: { data: { mood_type?: string; mood_score?: number }[] | null }) => {
        const last = data?.[0];
        const f = feelingById(last?.mood_type) ?? FEELINGS.find((x) => x.score === last?.mood_score);
        if (f) setSky((cur) => cur ?? f.id);
      });
    const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString();
    supabase.from('mission_reflections').select('*')
      .eq('user_id', profile.id).gte('created_at', weekAgo)
      .order('created_at', { ascending: false })
      .then(({ data, error }: { data: MissionReflection[] | null; error: unknown }) => {
        // Table may not exist until the migration runs; the home still works without it.
        if (!error && data) setReflections(data);
      });
  }, [profile]);

  const tName = firstName(therapistFull);
  const fromName = tName || 'המטפל/ת';
  const toName = tName || 'המטפל/ת שלך';

  const todayFeelings = useMemo(() => {
    const since = startOfToday();
    const map: Record<string, string> = {};
    for (const r of reflections) if (r.created_at >= since && r.mission_id && !map[r.mission_id]) map[r.mission_id] = r.feeling;
    return map;
  }, [reflections]);

  const latestHeart = useMemo(() => reflections.find((r) => r.therapist_heart_at), [reflections]);
  const birds = useMemo(
    () => reflections.filter((r) => r.therapist_heart_at).map((r) => ({ title: r.mission_title })),
    [reflections],
  );
  const seen = useMemo(() => reflections.some((r) => r.therapist_seen_at || r.therapist_heart_at), [reflections]);

  const sortedMissions = useMemo(
    () => [...missions].sort((a, b) => Number(a.is_completed) - Number(b.is_completed)),
    [missions],
  );
  const doneCount = missions.filter((m) => m.is_completed).length;

  const handleComplete = (m: Mission) => {
    completeMission(m.id, m.xp_reward);
    setFlightKey((k) => k + 1);
    setAnnounce(`"${m.title}" סומנה. זה יופיע אצל ${toName} לפני הפגישה.`);
    setAsking(m);
  };

  const handleFeeling = useCallback(async (feeling: FeelingId) => {
    const m = asking;
    setAsking(null);
    if (!m || !profile) return;
    const row = {
      user_id: profile.id,
      mission_id: String(m.id),
      mission_title: m.title,
      feeling,
    };
    setReflections((prev) => [{ ...row, id: -Date.now(), created_at: new Date().toISOString(), therapist_heart_at: null }, ...prev]);
    setSky(feeling);
    setAnnounce(`תודה. ${toName} יקבל/תקבל את זה.`);
    const { error } = await supabase.from('mission_reflections').insert(row);
    if (error) {
      // Before the migration: keep the signal by logging it as inner weather.
      const f = feelingById(feeling);
      await supabase.from('mood_logs').insert({ user_id: profile.id, mood_score: f?.score ?? 3, mood_type: feeling });
    }
  }, [asking, profile, toName]);

  const currentAvatar = localStorage.getItem('remind_avatar') || profile?.avatar_id || 'default';
  const currentXP     = profile?.xp ?? 0;
  const activeLevel   = LEVELS.reduce((acc, l) => (currentXP >= l.minDiamonds ? l.level : acc), 1);
  // Milestones the critter celebrates once: a new level, or five planks in one week.
  useEffect(() => {
    if (!profile || weekPlanks === undefined) return;
    try {
      const lastLevel = Number(localStorage.getItem('remind_last_level'));
      localStorage.setItem('remind_last_level', String(activeLevel));
      if (lastLevel > 0 && activeLevel > lastLevel) {
        setCelebrate(`עלית לגשר ${activeLevel}!`);
        return;
      }
      const week = weekStart(new Date()).toDateString();
      if (weekPlanks >= 5 && localStorage.getItem('remind_celebrated_week') !== week) {
        localStorage.setItem('remind_celebrated_week', week);
        setCelebrate('שבוע של גשר כמעט שלם!');
      }
    } catch { /* storage blocked: no celebration */ }
  }, [profile, activeLevel, weekPlanks]);

  const xpForLevel    = 500;
  const xpProgress    = Math.min(100, Math.round(((currentXP % xpForLevel) / xpForLevel) * 100));

  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'לילה טוב' : hour < 12 ? 'בוקר טוב' : hour < 18 ? 'צהריים טובים' : 'ערב טוב';

  if (loading) return (
    <div className="flex items-center justify-center h-full text-muted-foreground bg-background">
      <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 1.5 }}>
        טוען...
      </motion.div>
    </div>
  );

  if (!profile) return null;

  const tone = skyFor(sky, hour);
  const onSky = tone.dark ? 'text-white' : 'text-foreground';
  const chip = tone.dark
    ? 'bg-white/15 text-white border-white/25'
    : 'bg-white/60 text-foreground border-white/70';

  return (
    <div className="h-full overflow-y-auto bg-background dir-rtl">
      {/* ── The world: sky, two shores, the bridge ── */}
      <div className="relative">
        <ShoreScene
          feeling={sky}
          hour={hour}
          total={missions.length}
          done={doneCount}
          avatarId={currentAvatar}
          flightKey={flightKey}
          heart={!!latestHeart}
          welcomeBack={welcomeBack}
          hearts={birds}
          seen={seen}
          stones={stones}
          celebrate={celebrate}
          therapistName={toName}
          className="block w-full aspect-[390/360]"
        />
        <button
          onClick={() => {
            setSoundOn(!sound);
            setSound(!sound);
            if (!sound) play('drip');
          }}
          aria-pressed={sound}
          aria-label={sound ? 'צלילים פועלים, לכבות' : 'צלילים כבויים, להפעיל'}
          className={`absolute right-3 bottom-11 w-8 h-8 rounded-full border backdrop-blur flex items-center justify-center ${chip}`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 9v6h4l5 4V5L8 9z" fill="currentColor" />
            {sound ? <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" /> : <path d="M17 9l5 6M22 9l-5 6" />}
          </svg>
        </button>

        <div className="absolute inset-x-0 top-0 px-5 pt-5 flex items-start justify-between gap-3">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="min-w-0">
            <p className={`text-sm font-medium ${onSky} opacity-80`}>{greeting},</p>
            <h1 className={`text-[34px] leading-tight font-extrabold tracking-tight ${onSky} truncate`}>
              {profile.full_name?.split(' ')[0]}
            </h1>
            {missions.length > 0 && (
              <p className={`mt-1 inline-flex items-center gap-1.5 rounded-full border backdrop-blur px-3 py-1 text-xs font-bold ${chip}`}>
                <span className="w-2 h-2 rounded-full bg-bridge" aria-hidden="true" />
                {doneCount === missions.length
                  ? 'הגשר שלם להיום'
                  : `${doneCount} מתוך ${missions.length} קרשים הונחו`}
              </p>
            )}
          </motion.div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => navigate('/patient/bridges')}
              className={`h-9 px-2.5 rounded-full border backdrop-blur text-sm font-bold flex items-center gap-1 ${chip}`}
              aria-label={`${weekPlanks ?? 0} קרשים השבוע, לגשרים שלי`}
            >
              <BridgeNavIcon size={18} strokeWidth={2.2} />
              {weekPlanks ?? 0}
            </button>
            <button
              onClick={() => navigate('/patient/store')}
              className={`h-9 px-2.5 rounded-full border backdrop-blur text-sm font-bold flex items-center gap-1 ${chip}`}
              aria-label={`${profile.balance ?? 0} יהלומים, לחנות`}
            >
              <Glyph name="diamond" size={18} />
              {profile.balance ?? 0}
            </button>
            <button
              onClick={() => navigate('/patient/profile')}
              className={`w-9 h-9 rounded-full border backdrop-blur flex items-center justify-center text-lg ${chip}`}
              aria-label="הפרופיל שלי"
            >
              <Critter id={currentAvatar} size={30} />
            </button>
          </div>
        </div>
      </div>

      {/* ── The near shore: everything below the water ── */}
      <div className="relative -mt-8 rounded-t-[32px] bg-background px-4 pt-6 pb-28 flex flex-col gap-6">
        {latestHeart && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-accent/10 border border-accent/20 px-4 py-3 text-sm text-foreground flex items-center gap-3"
          >
            <Glyph name="heart" size={24} className="shrink-0" />
            <span>
              <span className="font-bold">{fromName}</span> השאיר/ה לב על "{latestHeart.mission_title}". הוא דולק מעל הקליניקה.
            </span>
          </motion.p>
        )}

        <SkyPicker userId={profile.id} value={sky} onPick={setSky} />

        <section aria-labelledby="notes-title" className="flex flex-col gap-5">
          <div className="flex items-baseline justify-between">
            <h2 id="notes-title" className="text-lg font-extrabold text-foreground">
              פתקים מ{fromName}
            </h2>
            {missions.length > 0 && (
              <span className="text-xs font-medium text-muted-foreground">{doneCount} / {missions.length} נשלחו בחזרה</span>
            )}
          </div>

          <AnimatePresence mode="popLayout">
            {sortedMissions.map((m, i) => (
              <NoteLetter
                key={m.id}
                mission={m}
                index={i}
                signature={fromName}
                feelingId={todayFeelings[String(m.id)]}
                onComplete={() => handleComplete(m)}
              />
            ))}
          </AnimatePresence>

          {missions.length === 0 && (
            <div className="paper-ruled rounded-md p-8 text-center shadow-sm">
              <p className="font-hand text-3xl text-primary">אין פתקים היום.</p>
              <p className="text-sm text-muted-foreground mt-1">אפשר לנשום, לשחק, או פשוט לנוח.</p>
            </div>
          )}
        </section>

        <button
          onClick={() => navigate('/patient/progress')}
          className="bg-card border border-border rounded-3xl px-4 py-3 text-start flex items-center gap-3 hover:border-primary/40 transition-colors"
        >
          <Glyph name="flag" size={28} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex justify-between text-xs font-bold text-foreground mb-1.5">
              <span>המסע שלי · רמה {activeLevel}</span>
              <span className="text-muted-foreground font-medium tabular-nums">{currentXP % xpForLevel}/{xpForLevel}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-secondary"
                initial={{ width: 0 }}
                animate={{ width: `${xpProgress}%` }}
                transition={{ duration: 0.6, delay: 0.2 }}
              />
            </div>
          </div>
          <Icon icon="solar:alt-arrow-left-linear" className="text-muted-foreground shrink-0" aria-hidden="true" />
        </button>

        <p className="font-hand text-center text-3xl text-muted-foreground px-6 leading-8">
          {quote}
        </p>
      </div>

      <div aria-live="polite" className="sr-only">{announce}</div>

      {/* ── Modals ── */}
      {asking && (
        <HowWasItSheet
          missionTitle={asking.title}
          therapistName={toName}
          onPick={handleFeeling}
          onSkip={() => setAsking(null)}
        />
      )}
      {showLevelUp && !asking && (
        <LevelUpModal newLevel={activeLevel} onClose={closeLevelUp} />
      )}
    </div>
  );
};

export default PatientHome;
